export interface ExifCoordinates {
  latitude: number;
  longitude: number;
}

export class ExifReader {
  static readExifGps(view: DataView): ExifCoordinates | null {
    // JPEG Magic Number
    if (view.getUint16(0) === 0xffd8) {
      return this.readJpegExif(view);
    }

    // PNG Magic Number
    if (view.getUint32(0) === 0x89504e47 && view.getUint32(4) === 0x0d0a1a0a) {
      return this.readPngExif(view);
    }

    return null;
  }

  static readExifDateTime(view: DataView): Date | null {
    if (view.getUint16(0) === 0xffd8) {
      return this.readJpegExifDateTime(view);
    }

    if (view.getUint32(0) === 0x89504e47 && view.getUint32(4) === 0x0d0a1a0a) {
      return this.readPngExifDateTime(view);
    }

    return null;
  }

  private static readJpegExif(view: DataView): ExifCoordinates | null {
    let offset = 2;
    const length = view.byteLength;

    while (offset < length) {
      if (view.getUint16(offset) !== 0xffe1) {
        offset += 2;
        const segmentLength = view.getUint16(offset);
        offset += segmentLength;
        continue;
      }

      const marker = view.getUint16(offset);
      if (marker !== 0xffe1) {
        offset += 2;
        continue;
      }

      const segmentLength = view.getUint16(offset + 2);
      const segmentData = new DataView(view.buffer, offset + 4, segmentLength - 2);

      const exifHeader = String.fromCharCode(
        segmentData.getUint8(0),
        segmentData.getUint8(1),
        segmentData.getUint8(2),
        segmentData.getUint8(3),
      );

      if (exifHeader === 'Exif') {
        const gpsData = this.parseExifGps(segmentData);
        if (gpsData) {
          return gpsData;
        }
      }

      offset += segmentLength + 2;
    }

    return null;
  }

  private static readPngExif(view: DataView): ExifCoordinates | null {
    let offset = 8;

    while (offset < view.byteLength - 8) {
      const chunkLength = view.getUint32(offset, false);
      offset += 4;

      if (chunkLength > view.byteLength - offset) {
        break;
      }

      const chunkType = String.fromCharCode(
        view.getUint8(offset),
        view.getUint8(offset + 1),
        view.getUint8(offset + 2),
        view.getUint8(offset + 3),
      );
      offset += 4;

      if (chunkType === 'eXIf') {
        const chunkData = new DataView(view.buffer, view.byteOffset + offset, chunkLength);
        const gpsData = this.parseExifGps(chunkData);
        if (gpsData) {
          return gpsData;
        }
      }

      offset += chunkLength + 4;

      if (chunkType === 'IEND') {
        break;
      }
    }

    return null;
  }

  private static parseExifGps(exifData: DataView): ExifCoordinates | null {
    try {
      const exifHeader = String.fromCharCode(
        exifData.getUint8(0),
        exifData.getUint8(1),
        exifData.getUint8(2),
        exifData.getUint8(3),
      );

      if (exifHeader !== 'Exif') {
        return null;
      }

      const byteOrder = exifData.getUint16(6);
      const isLittleEndian = byteOrder === 0x4949;

      const ifdOffsetRelative = exifData.getUint32(10, isLittleEndian);
      const ifdOffsetAbsolute = 6 + ifdOffsetRelative;

      if (ifdOffsetAbsolute + 2 > exifData.byteLength) {
        return null;
      }

      const mainIfdData = new DataView(
        exifData.buffer,
        exifData.byteOffset + ifdOffsetAbsolute,
        exifData.byteLength - ifdOffsetAbsolute,
      );
      const entryCount = mainIfdData.getUint16(0, isLittleEndian);

      let gpsIfdOffset: number | null = null;
      let exifIfdOffset: number | null = null;

      for (let i = 0; i < entryCount; i++) {
        const entryOffset = 2 + i * 12;
        const tag = mainIfdData.getUint16(entryOffset, isLittleEndian);
        const format = mainIfdData.getUint16(entryOffset + 2, isLittleEndian);
        const valueOrOffset = mainIfdData.getUint32(entryOffset + 8, isLittleEndian);

        if (tag === 0x8825) {
          if (format === 4) {
            const gpsIfdOffsetRelative = valueOrOffset;
            gpsIfdOffset = 6 + gpsIfdOffsetRelative;
            break;
          }
        }

        if (tag === 0x8769) {
          if (format === 4) {
            const exifIfdOffsetRelative = valueOrOffset;
            exifIfdOffset = 6 + exifIfdOffsetRelative;
          }
        }
      }

      if (!gpsIfdOffset && exifIfdOffset && exifIfdOffset + 2 <= exifData.byteLength) {
        const exifIfdData = new DataView(
          exifData.buffer,
          exifData.byteOffset + exifIfdOffset,
          exifData.byteLength - exifIfdOffset,
        );
        const exifEntryCount = exifIfdData.getUint16(0, isLittleEndian);

        for (let i = 0; i < exifEntryCount; i++) {
          const entryOffset = 2 + i * 12;
          const tag = exifIfdData.getUint16(entryOffset, isLittleEndian);
          const format = exifIfdData.getUint16(entryOffset + 2, isLittleEndian);
          const valueOrOffset = exifIfdData.getUint32(entryOffset + 8, isLittleEndian);

          if (tag === 0x8825) {
            if (format === 4) {
              const gpsIfdOffsetRelative = valueOrOffset;
              gpsIfdOffset = 6 + gpsIfdOffsetRelative;
              break;
            }
          }
        }
      }

      if (!gpsIfdOffset || gpsIfdOffset + 2 > exifData.byteLength) {
        return null;
      }

      const gpsIfdData = new DataView(
        exifData.buffer,
        exifData.byteOffset + gpsIfdOffset,
        exifData.byteLength - gpsIfdOffset,
      );
      const gpsEntryCount = gpsIfdData.getUint16(0, isLittleEndian);

      let gpsLatitude: number[] | null = null;
      let gpsLatitudeRef: string | null = null;
      let gpsLongitude: number[] | null = null;
      let gpsLongitudeRef: string | null = null;

      for (let i = 0; i < gpsEntryCount; i++) {
        const entryOffset = 2 + i * 12;
        const tag = gpsIfdData.getUint16(entryOffset, isLittleEndian);

        if (tag === 0x0002) {
          const format = gpsIfdData.getUint16(entryOffset + 2, isLittleEndian);
          const components = gpsIfdData.getUint32(entryOffset + 4, isLittleEndian);
          if (format === 5 && components === 3) {
            const valueOffsetRelative = gpsIfdData.getUint32(entryOffset + 8, isLittleEndian);
            const valueOffsetAbsolute = 6 + valueOffsetRelative;

            if (valueOffsetAbsolute + 24 <= exifData.byteLength) {
              const valueData = new DataView(exifData.buffer, exifData.byteOffset + valueOffsetAbsolute, 24);
              gpsLatitude = [
                this.readRational(valueData, 0, isLittleEndian),
                this.readRational(valueData, 8, isLittleEndian),
                this.readRational(valueData, 16, isLittleEndian),
              ];
            }
          }
        }

        if (tag === 0x0001) {
          const format = gpsIfdData.getUint16(entryOffset + 2, isLittleEndian);
          if (format === 2) {
            const valueOffsetRelative = gpsIfdData.getUint32(entryOffset + 8, isLittleEndian);
            const valueOffsetAbsolute = 6 + valueOffsetRelative;
            if (valueOffsetAbsolute < exifData.byteLength) {
              gpsLatitudeRef = String.fromCharCode(exifData.getUint8(valueOffsetAbsolute));
            }
          }
        }

        if (tag === 0x0004) {
          const format = gpsIfdData.getUint16(entryOffset + 2, isLittleEndian);
          const components = gpsIfdData.getUint32(entryOffset + 4, isLittleEndian);
          if (format === 5 && components === 3) {
            const valueOffsetRelative = gpsIfdData.getUint32(entryOffset + 8, isLittleEndian);
            const valueOffsetAbsolute = 6 + valueOffsetRelative;

            if (valueOffsetAbsolute + 24 <= exifData.byteLength) {
              const valueData = new DataView(exifData.buffer, exifData.byteOffset + valueOffsetAbsolute, 24);
              gpsLongitude = [
                this.readRational(valueData, 0, isLittleEndian),
                this.readRational(valueData, 8, isLittleEndian),
                this.readRational(valueData, 16, isLittleEndian),
              ];
            }
          }
        }

        if (tag === 0x0003) {
          const format = gpsIfdData.getUint16(entryOffset + 2, isLittleEndian);
          if (format === 2) {
            const valueOffsetRelative = gpsIfdData.getUint32(entryOffset + 8, isLittleEndian);
            const valueOffsetAbsolute = 6 + valueOffsetRelative;
            if (valueOffsetAbsolute < exifData.byteLength) {
              gpsLongitudeRef = String.fromCharCode(exifData.getUint8(valueOffsetAbsolute));
            }
          }
        }
      }

      if (gpsLatitude && gpsLongitude) {
        let lat = gpsLatitude[0] + gpsLatitude[1] / 60 + gpsLatitude[2] / 3600;
        let lon = gpsLongitude[0] + gpsLongitude[1] / 60 + gpsLongitude[2] / 3600;

        if (gpsLatitudeRef === 'S') lat = -lat;
        if (gpsLongitudeRef === 'W') lon = -lon;

        return { latitude: lat, longitude: lon };
      }
    } catch (error) {}

    return null;
  }

  private static readRational(dataView: DataView, offset: number, isLittleEndian: boolean): number {
    const numerator = dataView.getUint32(offset, isLittleEndian);
    const denominator = dataView.getUint32(offset + 4, isLittleEndian);
    return denominator !== 0 ? numerator / denominator : 0;
  }

  private static readJpegExifDateTime(view: DataView): Date | null {
    let offset = 2;
    const length = view.byteLength;

    while (offset < length) {
      const marker = view.getUint16(offset);

      if (marker >= 0xffe0 && marker <= 0xffef) {
        const segmentLength = view.getUint16(offset + 2);

        if (offset + 4 + segmentLength - 2 > length) {
          offset += segmentLength + 2;
          continue;
        }

        const segmentData = new DataView(view.buffer, offset + 4, segmentLength - 2);

        if (segmentData.byteLength >= 4) {
          const header = String.fromCharCode(
            segmentData.getUint8(0),
            segmentData.getUint8(1),
            segmentData.getUint8(2),
            segmentData.getUint8(3),
          );

          if (header === 'Exif') {
            const dateTime = this.parseExifDateTime(segmentData);
            if (dateTime) {
              return dateTime;
            }
          }

          if (header === 'http' || segmentData.byteLength > 30) {
            let xmpHeader = '';
            for (let i = 0; i < Math.min(30, segmentData.byteLength); i++) {
              xmpHeader += String.fromCharCode(segmentData.getUint8(i));
            }
            if (xmpHeader.includes('http://ns.adobe.com/xap/1.0/') || xmpHeader.includes('x:xmpmeta')) {
              const xmpDate = this.parseXmpDate(segmentData);
              if (xmpDate) {
                return xmpDate;
              }
            }
          }
        }

        offset += segmentLength + 2;
      } else {
        offset += 2;
        if (offset + 2 > length) break;
        const segmentLength = view.getUint16(offset);
        offset += segmentLength;
      }
    }

    return null;
  }

  private static parseXmpDate(xmpData: DataView): Date | null {
    try {
      let xmpString = '';
      for (let i = 0; i < xmpData.byteLength; i++) {
        const char = String.fromCharCode(xmpData.getUint8(i));
        if (char === '\0') break;
        xmpString += char;
      }

      const datePatterns = [
        /exif:DateTimeOriginal="([^"]+)"/i,
        /photoshop:DateCreated="([^"]+)"/i,
        /xmp:CreateDate="([^"]+)"/i,
        /xmp:ModifyDate="([^"]+)"/i,
        /dc:date="([^"]+)"/i,
        /<exif:DateTimeOriginal>([^<]+)<\/exif:DateTimeOriginal>/i,
        /<photoshop:DateCreated>([^<]+)<\/photoshop:DateCreated>/i,
      ];

      for (const pattern of datePatterns) {
        const match = xmpString.match(pattern);
        if (match && match[1]) {
          const date = this.parseDateString(match[1]);
          if (date) {
            return date;
          }
        }
      }
    } catch (error) {}

    return null;
  }

  private static parseDateString(dateStr: string): Date | null {
    try {
      let date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date;
      }

      const exifMatch = dateStr.match(/(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
      if (exifMatch) {
        const year = parseInt(exifMatch[1], 10);
        const month = parseInt(exifMatch[2], 10) - 1;
        const day = parseInt(exifMatch[3], 10);
        const hour = parseInt(exifMatch[4], 10);
        const minute = parseInt(exifMatch[5], 10);
        const second = parseInt(exifMatch[6], 10);
        date = new Date(Date.UTC(year, month, day, hour, minute, second));
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    } catch (error) {}

    return null;
  }

  private static readPngExifDateTime(view: DataView): Date | null {
    let offset = 8;

    while (offset < view.byteLength - 8) {
      const chunkLength = view.getUint32(offset, false);
      offset += 4;

      if (chunkLength > view.byteLength - offset) {
        break;
      }

      const chunkType = String.fromCharCode(
        view.getUint8(offset),
        view.getUint8(offset + 1),
        view.getUint8(offset + 2),
        view.getUint8(offset + 3),
      );
      offset += 4;

      if (chunkType === 'eXIf') {
        const chunkData = new DataView(view.buffer, view.byteOffset + offset, chunkLength);
        const dateTime = this.parseExifDateTime(chunkData);
        if (dateTime) {
          return dateTime;
        }
      }

      offset += chunkLength + 4;

      if (chunkType === 'IEND') {
        break;
      }
    }

    return null;
  }

  private static parseExifDateTime(exifData: DataView): Date | null {
    try {
      const exifHeader = String.fromCharCode(
        exifData.getUint8(0),
        exifData.getUint8(1),
        exifData.getUint8(2),
        exifData.getUint8(3),
      );

      if (exifHeader !== 'Exif') {
        return null;
      }

      const byteOrder = exifData.getUint16(6);
      const isLittleEndian = byteOrder === 0x4949;

      const ifdOffsetRelative = exifData.getUint32(10, isLittleEndian);
      const ifdOffsetAbsolute = 6 + ifdOffsetRelative;

      if (ifdOffsetAbsolute + 2 > exifData.byteLength) {
        return null;
      }

      const mainIfdData = new DataView(
        exifData.buffer,
        exifData.byteOffset + ifdOffsetAbsolute,
        exifData.byteLength - ifdOffsetAbsolute,
      );
      const entryCount = mainIfdData.getUint16(0, isLittleEndian);

      let dateTimeString: string | null = null;
      let exifIfdOffset: number | null = null;

      for (let i = 0; i < entryCount; i++) {
        const entryOffset = 2 + i * 12;
        const tag = mainIfdData.getUint16(entryOffset, isLittleEndian);
        const format = mainIfdData.getUint16(entryOffset + 2, isLittleEndian);
        const components = mainIfdData.getUint32(entryOffset + 4, isLittleEndian);
        const valueOrOffset = mainIfdData.getUint32(entryOffset + 8, isLittleEndian);

        if (tag === 0x0132 && format === 2 && components === 20) {
          const valueOffsetRelative = valueOrOffset;
          const valueOffsetAbsolute = 6 + valueOffsetRelative;
          if (valueOffsetAbsolute + 20 <= exifData.byteLength) {
            let dateStr = '';
            for (let j = 0; j < 19; j++) {
              const char = String.fromCharCode(exifData.getUint8(valueOffsetAbsolute + j));
              if (char === '\0') break;
              dateStr += char;
            }
            if (dateStr.length > 0) {
              dateTimeString = dateStr;
              break;
            }
          }
        }

        if (tag === 0x8769 && format === 4) {
          exifIfdOffset = 6 + valueOrOffset;
        }
      }

      if (!dateTimeString && exifIfdOffset && exifIfdOffset + 2 <= exifData.byteLength) {
        const exifIfdData = new DataView(
          exifData.buffer,
          exifData.byteOffset + exifIfdOffset,
          exifData.byteLength - exifIfdOffset,
        );
        const exifEntryCount = exifIfdData.getUint16(0, isLittleEndian);

        for (let i = 0; i < exifEntryCount; i++) {
          const entryOffset = 2 + i * 12;
          const tag = exifIfdData.getUint16(entryOffset, isLittleEndian);
          const format = exifIfdData.getUint16(entryOffset + 2, isLittleEndian);
          const components = exifIfdData.getUint32(entryOffset + 4, isLittleEndian);
          const valueOrOffset = exifIfdData.getUint32(entryOffset + 8, isLittleEndian);

          if (tag === 0x9003 && format === 2 && components === 20) {
            const valueOffsetRelative = valueOrOffset;
            const valueOffsetAbsolute = 6 + valueOffsetRelative;
            if (valueOffsetAbsolute + 20 <= exifData.byteLength) {
              let dateStr = '';
              for (let j = 0; j < 19; j++) {
                const char = String.fromCharCode(exifData.getUint8(valueOffsetAbsolute + j));
                if (char === '\0') break;
                dateStr += char;
              }
              if (dateStr.length > 0) {
                dateTimeString = dateStr;
                break;
              }
            }
          }
        }
      }

      if (dateTimeString) {
        const parts = dateTimeString.split(' ');
        if (parts.length === 2) {
          const datePart = parts[0].split(':');
          const timePart = parts[1].split(':');
          if (datePart.length === 3 && timePart.length === 3) {
            const year = parseInt(datePart[0], 10);
            const month = parseInt(datePart[1], 10) - 1;
            const day = parseInt(datePart[2], 10);
            const hour = parseInt(timePart[0], 10);
            const minute = parseInt(timePart[1], 10);
            const second = parseInt(timePart[2], 10);

            const date = new Date(Date.UTC(year, month, day, hour, minute, second));
            if (!isNaN(date.getTime())) {
              return date;
            }
          }
        }
      }
    } catch (error) {}

    return null;
  }
}
